import React, { useEffect, useMemo, useState, useId } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { getCompanySetting, putCompanySetting } from '../../../api/company/company';
import { useTheme } from '@mui/material/styles';
import './PrivacySettings/PrivacySettings.css';

/**
 * @param {{ variant?: 'page' | 'embedded' }} props
 */
const PrivacySettings = ({ variant = 'page' }) => {
  const embedded = variant === 'embedded';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user, companyData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { setNotification } = useNotification();
  const [allowImpersonation, setAllowImpersonation] = useState(true);
  const [impersonationHelpOpen, setImpersonationHelpOpen] = useState(false);
  const impersonationHelpPanelId = useId();

  const cssVars = useMemo(() => {
    const surface = isDark ? '#0f172a' : '#ffffff';
    const surface2 = isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.7)';
    const text = isDark ? '#e5e7eb' : '#0f172a';
    const muted = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.12)';
    const shadow = isDark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 10px 30px rgba(2, 6, 23, 0.08)';
    const shimmerA = isDark ? 'rgba(226, 232, 240, 0.08)' : 'rgba(15, 23, 42, 0.06)';
    const shimmerB = isDark ? 'rgba(226, 232, 240, 0.14)' : 'rgba(15, 23, 42, 0.12)';

    return {
      '--ps-surface': surface,
      '--ps-surface-2': surface2,
      '--ps-text': text,
      '--ps-muted': muted,
      '--ps-border': border,
      '--ps-shadow': shadow,
      '--ps-focus': 'rgba(72, 138, 236, 0.45)',
      '--ps-shimmer-a': shimmerA,
      '--ps-shimmer-b': shimmerB
    };
  }, [isDark]);

  useEffect(() => {
    const companyId = companyData?.id || user?.companyId;
    if (companyId) {
      const fetchSettings = async () => {
        try {
          const data = await getCompanySetting({ companyId, key: 'general' });
          setAllowImpersonation(data.allowImpersonation !== false);
        } catch (error) {
          console.error('Error fetching company settings:', error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [user, companyData?.id]);

  const handleImpersonationToggle = async (e) => {
    const newValue = e.target.checked;
    setAllowImpersonation(newValue);
    
    try {
      if (!user?.companyId && !companyData?.id) throw new Error('Missing company');
      setSaving(true);
      await putCompanySetting({
        companyId: companyData?.id || user.companyId,
        key: 'general',
        settings: {
          allowImpersonation: newValue,
          updatedAt: new Date().toISOString(),
        },
      });

      setNotification({ 
        open: true, 
        message: `Admin impersonation has been ${newValue ? 'enabled' : 'disabled'}`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error updating impersonation settings:', error);
      setAllowImpersonation(!newValue); // Revert the toggle if failed
      setNotification({ 
        open: true, 
        message: 'Failed to update impersonation settings', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    const loadingBody = embedded ? (
      <div className="psEmbeddedLoading" aria-busy="true" aria-live="polite">
        <div className="psShimmerLine short" />
        <div className="psShimmerLine medium" />
      </div>
    ) : (
      <div className="psLoading" aria-busy="true" aria-live="polite">
        <div className="psLoadingCard">
          <div className="psShimmerLine short" />
          <div className="psShimmerLine medium" />
          <div className="psShimmerLine" />
          <div className="psShimmerLine" />
        </div>
      </div>
    );
    if (embedded) {
      return (
        <div className="privacySettingsEmbedded" style={cssVars}>
          {loadingBody}
        </div>
      );
    }
    return (
      <div className="privacySettingsPage" style={cssVars}>
        <div className="privacySettingsInner">
          {loadingBody}
        </div>
      </div>
    );
  }

  const fieldsBody = (
    <>
      <label
        className={`ps-check-row${saving || !(companyData?.id || user?.companyId) ? ' ps-check-row--disabled' : ''}`}
      >
        <input
          type="checkbox"
          className="ps-checkbox"
          checked={allowImpersonation}
          onChange={handleImpersonationToggle}
          disabled={saving || !(companyData?.id || user?.companyId)}
          aria-describedby="ps-impersonation-desc"
        />
        <span className="ps-check-row__body">
          <span className="ps-check-row__title">Allow admin impersonation</span>
          <span id="ps-impersonation-desc" className="ps-check-row__desc">
            {allowImpersonation
              ? 'Support staff can access your account when needed.'
              : 'Support staff cannot access your account.'}
            {saving ? ' Updating…' : ''}
          </span>
        </span>
      </label>

      <div className={embedded ? 'psEmbeddedHelp' : 'psHelpLine'}>
        {embedded ? (
          <button
            type="button"
            className="psEmbeddedHelpLink"
            aria-expanded={impersonationHelpOpen}
            aria-controls={impersonationHelpPanelId}
            onClick={() => setImpersonationHelpOpen((v) => !v)}
          >
            {impersonationHelpOpen ? 'Hide details' : 'What is impersonation?'}
          </button>
        ) : (
          <>
            <span className="psHelpLine__label">What&apos;s impersonation?</span>
            <button
              type="button"
              className="psHelpTrigger"
              aria-expanded={impersonationHelpOpen}
              aria-controls={impersonationHelpPanelId}
              aria-label={
                impersonationHelpOpen
                  ? 'Hide explanation of admin impersonation'
                  : 'Show explanation of admin impersonation'
              }
              onClick={() => setImpersonationHelpOpen((v) => !v)}
            >
              <svg className="psHelpIcon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 17h.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M9.5 9.75a2.75 2.75 0 115 0c0 2.25-2.25 2-2.25 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </button>
          </>
        )}
      </div>

      {impersonationHelpOpen && (
        <div
          id={impersonationHelpPanelId}
          className={embedded ? 'psEmbeddedHelpPanel' : 'psHelpPanel'}
          role="region"
        >
          <p className="psText">
            This feature allows our support team to temporarily view your account as you see it, helping them
            resolve issues more efficiently. All sessions are logged and monitored.
          </p>
        </div>
      )}
    </>
  );

  const mainSection = (
    <section className="psCard" aria-label="Impersonation settings">
      <div className="psCardBody">{fieldsBody}</div>
    </section>
  );

  if (embedded) {
    return (
      <div className="privacySettingsEmbedded" style={cssVars}>
        {fieldsBody}
      </div>
    );
  }

  return (
    <div className="privacySettingsPage" style={cssVars}>
      <div className="privacySettingsInner">
        <div className="privacySettingsHeader">
          <h1>Privacy Settings</h1>
          <p>Control account access features like admin impersonation for support.</p>
        </div>

        {mainSection}
      </div>
    </div>
  );
};

export default PrivacySettings;
